class WereldRadial extends HTMLElement {
  connectedCallback() {
    this.geinitialiseerd = false;
    this.controls = this.querySelector('[data-wr-controls]');

    this.pilIsGeenSleepgreep();
    this.kaartKlik();

    /* Reduced motion: standaard een gewone scrollbare rij, en geen 100kb
       scripts inladen voor een effect dat de bezoeker heeft uitgezet.

       Dit is de meest voorkomende reden dat de slider op een telefoon een
       platte rij is en op een laptop niet: "Verminder beweging" staat op
       veel toestellen aan. Er zit geen breedtegrens in deze code.

       Met data-wr-negeer-reduced op de sectie draait hij alsnog. Dat is een
       bewuste keuze van de winkel, niet de standaard: iemand die deze
       instelling aanzet heeft gevraagd om minder beweging, en een cirkel
       van kaarten die met inertie doorslingert is precies waarvoor die
       instelling bedoeld is. De kaarten zijn navigatie en werken in beide
       gevallen; alleen de manier van bewegen verschilt. */
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.negeerReduced = this.hasAttribute('data-wr-negeer-reduced');

    if (this.reduced.matches && !this.negeerReduced) {
      this.setAttribute('data-wr-modus', 'statisch');
      return;
    }

    this.setAttribute('data-wr-modus', 'wachten');

    // In de theme editor meteen initialiseren: daar wil je direct zien wat je doet
    if (window.Shopify?.designMode) {
      this.init();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.observer.disconnect();
          this.init();
        }
      },
      { rootMargin: '400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.kloonObserver?.disconnect();
    this.opruimen();
  }

  async init() {
    if (this.geinitialiseerd) return;
    this.geinitialiseerd = true;

    try {
      // Volgorde is belangrijk: core eerst, dan de plugins, dan het slider-script
      await this.laadScript(this.dataset.gsapCore, 'gsap');
      await Promise.all([
        this.laadScript(this.dataset.gsapDraggable, 'Draggable'),
        this.laadScript(this.dataset.gsapInertia, 'InertiaPlugin'),
        this.laadScript(this.dataset.gsapCustomease, 'CustomEase'),
      ]);
      await this.laadScript(this.dataset.radialScript, 'initRadialCardsSlider');

      if (typeof window.initRadialCardsSlider !== 'function') {
        throw new Error('initRadialCardsSlider niet gevonden in radial-slider.js');
      }

      // Modus MOET voor de init omgezet worden. Het script leest
      // transform-origin en de kaartafmetingen uit om de cirkel te berekenen,
      // en die staan alleen in de actieve modus in de CSS. Zet je dit erna,
      // dan meet het script 50% 50% en staat de hele cirkel verkeerd.
      this.setAttribute('data-wr-modus', 'actief');
      void this.offsetHeight; // reflow forceren zodat de CSS toegepast is

      window.initRadialCardsSlider();

      this.bewaakKlonen();

      if (this.controls) this.controls.hidden = false;

      this.sleepUitOpTouch();
    } catch (err) {
      // Terugvalpad: de kaarten blijven een leesbare, scrollbare rij met links
      console.warn('[wereld-radial] slider niet geladen, terugval op statische rij', err);
      this.setAttribute('data-wr-modus', 'statisch');
      if (this.controls) this.controls.hidden = true;
    }
  }

  /* ==========================================================================
     Slepen uitzetten op een aanraakscherm
     ==========================================================================
     Op een telefoon nam deze sectie de verticale scroll over. Stopte je met
     je duim op de kaarten, dan kon je niet verder scrollen en zat je vast.

     De oorzaak zit in de Draggable van GSAP. Die staat op type "rotation",
     dus hij kan in beide richtingen bewegen, en dan roept hij bij de eerste
     touchmove preventDefault op om de gebaren zelf te kunnen lezen. Voor een
     draaibeweging is dat logisch: hij weet niet vooraf of jouw veeg bedoeld
     is om te draaien.

     Ik heb eerst geprobeerd dat met CSS te regelen, met touch-action: pan-y.
     Dat is de juiste oplossing bij een slider die maar één as gebruikt, maar
     bij type "rotation" hielp het niet. GSAP heeft daar zelf een instelling
     voor, allowNativeTouchScrolling, en die werkt uitsluitend bij type "x"
     of "y". Bij rotatie kan de bibliotheek de as niet vrijgeven.

     Dus zetten we het slepen uit zodra het een aanraakscherm is. Wat blijft:
     de cirkel, de animatie en de navigatie via de twee pijlen en de vijf
     bolletjes, die bij init toch al zichtbaar worden. Wat weggaat: met je
     vinger aan de cirkel draaien.

     Dat is een bewuste ruil. Vastzitten op een sectie is een fout, niet
     kunnen vegen is een gemis.

     De mediaquery en niet een test op touch-ondersteuning: een laptop met
     touchscreen ondersteunt aanraken maar heeft een muis, en daar mag het
     slepen gewoon blijven. hover: none met pointer: coarse betekent een
     toestel waar de vinger het enige aanwijsmiddel is.

     ---------------------------------------------------------------------------
     Wat er in de plaats komt: één as, door onszelf gelezen
     ---------------------------------------------------------------------------
     Alleen het slepen uitzetten was niet genoeg, want dan kan je op een
     telefoon de cirkel helemaal niet meer met je vinger draaien. Dus doen we
     het gebaar zelf, met pointer events, en op één as.

     Het mechanisme staat of valt bij twee dingen samen:

     touch-action: pan-y pinch-zoom in de CSS
       Zegt tegen de browser: verticaal pannen is van jou, horizontaal doe ik
       niet. De browser hoeft dan niet op JavaScript te wachten voor een
       verticale veeg en begint meteen te scrollen.

     wij roepen nergens preventDefault
       Daardoor kunnen we een verticale veeg ook niet per ongeluk tegenhouden.
       Dat was precies de fout van de Draggable op type "rotation": die riep
       het wel, en dan is de scroll voor dat gebaar verloren.

     Het gevolg is een nette verdeling. Veeg je verticaal, dan scrollt de
     pagina en krijgen wij een pointercancel, waarna we stoppen. Veeg je
     horizontaal, dan doet de browser niets en draaien wij de cirkel.

     De rekenregel: een veeg over de volle breedte van de sectie is 180
     graden. Zo voelt het op elk scherm hetzelfde, of het nu een telefoon van
     390px is of een tablet van 800px.

     Bij het lossen snappen we naar de dichtstbijzijnde kaart. Die snapfunctie
     lenen we van de Draggable die we net gesloopt hebben: die zat in
     vars.snap en rekent rotatie naar de dichtstbijzijnde stap. Zo hoeven we
     het aantal kaarten en de stapgrootte niet zelf te kennen.

     Slaagt een van de voorwaarden niet, dan valt hij terug op alleen de
     pijlen en de bolletjes. Nooit op een sectie waar je vast zit: dat is
     hier de enige uitkomst die echt fout is. */
  sleepUitOpTouch() {
    if (!window.matchMedia('(hover: none) and (pointer: coarse)').matches) return;

    /* Draggable.create geeft in de meeste versies een array terug, maar in
       dit script komt er één instantie uit. Beide gevallen afdekken, anders
       staat er straks stil een fout in de console. */
    const bron = this._radialSliderDraggable;
    const instanties = Array.isArray(bron) ? bron : bron ? [bron] : [];
    const oud = instanties[0];

    /* Geen nieuwe Draggable sinds de vorige keer: een resize waarbij alleen
       de hoogte veranderde (de adresbalk van een telefoon bij het scrollen).
       Dan heeft het Osmo-script niets opnieuw opgebouwd en loopt onze eigen
       swipe gewoon nog; niet onterecht op "uit" zetten. */
    if (!oud && this._swipeAan) return;

    /* Alles wat we van het Osmo-script nodig hebben, ophalen voor we het
       slopen. Daarna is het weg. */
    const render = oud && oud.vars && typeof oud.vars.onDrag === 'function' ? oud.vars.onDrag : null;
    const snap = oud && oud.vars && typeof oud.vars.snap === 'function' ? oud.vars.snap : null;
    const doel = oud ? oud.target : null;

    /* De rotation-Draggable moet in alle gevallen weg: zolang die leeft,
       blijft hij touchmove afbreken. */
    instanties.forEach((i) => i?.kill?.());
    this._radialSliderDraggable = null;

    if (!render || !doel || !window.gsap) {
      /* Geen eigen swipe mogelijk. Dan alleen de knoppen, en alles terug aan
         de browser. */
      this.setAttribute('data-wr-sleep', 'uit');
      return;
    }

    this.setAttribute('data-wr-sleep', 'eigen');
    this.eigenSwipe(doel, render, snap);

    /* Het Osmo-script bouwt de cirkel opnieuw op bij een resize en maakt dan
       ook een nieuwe Draggable aan. Zonder deze herhaling staat het slepen na
       een draai van het toestel weer aan. */
    if (this._sleepWacht) return;
    this._sleepWacht = true;

    const opnieuw = () => {
      clearTimeout(this._sleepTimer);
      this._sleepTimer = setTimeout(() => this.sleepUitOpTouch(), 250);
    };

    window.addEventListener('resize', opnieuw, { passive: true });
    window.addEventListener('orientationchange', opnieuw, { passive: true });
  }

  /* De hoek per kaart uit de snapfunctie halen. Die functie rondt een rotatie
     af naar de dichtstbijzijnde stap, dus we lopen van 0 omhoog tot de
     uitkomst voor het eerst verspringt. Dat verschil is de stap.

     Gemeten op deze winkel: 12 graden per stap. */
  snapStap(snap) {
    if (typeof snap !== 'function') return 0;
    try {
      const begin = snap(0);
      for (let hoek = 1; hoek <= 360; hoek += 1) {
        const uit = snap(hoek);
        if (uit !== begin) return Math.abs(uit - begin);
      }
    } catch (e) {
      /* Een snapfunctie die op onze proefwaarden struikelt: dan liever geen
         stap dan een verkeerde. De aanroeper heeft een vangnet. */
    }
    return 0;
  }

  /* Het gebaar zelf. Zie de uitleg bij sleepUitOpTouch waarom hier nergens
     preventDefault staat, en waarom dat de kern van de oplossing is. */
  eigenSwipe(doel, render, snap) {
    /* Altijd de laatste opbouw bewaren. Bij een breedtewijziging (toestel
       draaien) maakt het Osmo-script een nieuwe proxy met een nieuwe render-
       en snapfunctie. De luisteraars hieronder lezen daarom via this._swipe:
       anders draait een veeg daarna nog de oude proxy, die al uit de DOM is,
       terwijl de knoppen de nieuwe gebruiken. */
    this._swipe = { doel, render, snap };
    if (this._swipeAan) return;
    this._swipeAan = true;

    const gsap = window.gsap;

    /* Meten en luisteren doen we beide op de sectie, en dat is de tweede
       correctie op de gevoeligheid.

       Ik nam eerst de breedte van .wr__list als maat voor "een veeg over de
       volle breedte". Dat is fout: in de actieve modus is die baan maar 196px
       breed terwijl de sectie 491px is, want de kaarten staan in een cirkel
       die buiten die baan uitsteekt. Elke veeg werd dus met factor 2,5 te
       zwaar doorgerekend. Samen met zes stappen in plaats van twee liep de
       gevoeligheid een factor zeven te hoog op.

       Luisteren op de sectie is ook nodig: het Osmo-script kloont kaarten
       voor de loop, en die klonen komen niet noodzakelijk in dezelfde baan
       terecht. Landt je duim op een kloon buiten die baan, dan bereikt
       pointerdown onze luisteraar niet. */
    const vlak = this;
    const luisterVlak = this;

    /* Hoe snel de cirkel meedraait met je vinger.

       Niet in graden per pixel, maar in kaarten per veeg over de volle
       breedte. Zo voelt het op elk scherm hetzelfde, en het blijft kloppen
       als er kaarten bij komen.

       De stapgrootte vragen we aan de snapfunctie van het Osmo-script in
       plaats van hem hier vast te zetten. Gemeten op deze winkel: 12 graden
       per stap. Komen er kaarten bij, dan wordt die stap kleiner en volgt
       dit mee.

       Eén stap is hier ook precies één kaart: de snapfunctie snapt op 12
       graden en --slider-rotate, de hoek tussen twee kaarten, staat op mobiel
       eveneens op 12 graden. Nagemeten, niet aangenomen.

       Twee kaarten over de volle breedte van de sectie. Dat is bewust rustig.
       Ik had eerst zes, en dat vloog door de kaarten: bij zes haal je met één
       duimgebaar een halve cirkel om en weet je niet meer waar je bent.

       Voelt het te traag, zet dit op 3. Hoger dan 4 wordt het weer vluchtig. */
    const STAPPEN_PER_VEEG = 2;
    /* Per gebaar opnieuw bepaald: de stap volgt de laatste opbouw (op mobiel
       en desktop staat --slider-rotate anders). */
    const gradenPerBreedte = () => {
      const stap = this.snapStap(this._swipe.snap);
      return stap ? stap * STAPPEN_PER_VEEG : 72;
    };

    let bezig = false;
    let startX = 0;
    let startRotatie = 0;
    let pointerId = null;
    let GRADEN_PER_BREEDTE = 72;

    const rotatieNu = () => Number(gsap.getProperty(this._swipe.doel, 'rotation')) || 0;

    const neer = (e) => {
      if (e.pointerType !== 'touch') return;
      if (bezig) return;
      bezig = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startRotatie = rotatieNu();
      GRADEN_PER_BREEDTE = gradenPerBreedte();
      gsap.killTweensOf(this._swipe.doel);
    };

    const beweeg = (e) => {
      if (!bezig || e.pointerId !== pointerId) return;
      const { doel, render } = this._swipe;
      const breedte = vlak.getBoundingClientRect().width || window.innerWidth;
      const graden = ((e.clientX - startX) / breedte) * GRADEN_PER_BREEDTE;
      gsap.set(doel, { rotation: startRotatie + graden });
      render();
    };

    const los = (e) => {
      if (!bezig || (e && e.pointerId !== pointerId)) return;
      bezig = false;
      pointerId = null;

      const { doel, render, snap } = this._swipe;
      if (!snap) return;

      const doelRotatie = snap(rotatieNu());
      gsap.to(doel, {
        rotation: doelRotatie,
        duration: 0.45,
        ease: 'power3.out',
        onUpdate: render,
        onComplete: render,
      });
    };

    /* pointerdown op het vlak, de rest op window: laat je je vinger buiten
       de kaarten los, dan hoort de beweging alsnog netjes af te lopen.

       passive: true op alle drie. Dat is niet alleen voor de prestaties: het
       maakt het onmogelijk om per ongeluk preventDefault te roepen, en juist
       dat moest hier uitgesloten worden. */
    luisterVlak.addEventListener('pointerdown', neer, { passive: true });
    window.addEventListener('pointermove', beweeg, { passive: true });
    window.addEventListener('pointerup', los, { passive: true });

    /* pointercancel is het geval dat we willen: de browser heeft besloten dat
       dit een verticale scroll is en neemt het gebaar over. Wij laten los. */
    window.addEventListener('pointercancel', los, { passive: true });
  }

  /* ==========================================================================
     De pil is de link, de rest van de kaart is om te slepen (en te klikken)
     ==========================================================================
     Vroeger was de hele kaart een link. Dan moest Draggable bij elke klik
     raden of het een klik of een sleep was, en een korte sleep eindigde soms
     op een andere pagina. Zie kaartKlik voor hoe een klik op de kaart nu
     alsnog werkt, zonder die verwarring.

     Nu is alleen de pil een <a>. Een druk op de pil mag de sleep niet starten:
     we vangen pointerdown, mousedown en touchstart in de capture-fase op dit
     element op en stoppen de propagatie. Het Osmo-script luistert op de lijst,
     dieper in de boom, dus die ziet de druk nooit. Onze eigen touch-swipe
     luistert in de bubble-fase op dit element en ziet hem dus ook niet.

     Gevolg: tikken of klikken op de pil navigeert altijd, en slepen begint
     overal elders op de kaart. Geen preventDefault, dus de klik zelf en het
     verticale scrollen blijven volledig van de browser.

     Via delegatie op dit element, zodat het ook werkt op de klonen die het
     Osmo-script bij elke resize opnieuw aanmaakt. */
  pilIsGeenSleepgreep() {
    if (this._pilBewaakt) return;
    this._pilBewaakt = true;

    const stop = (e) => {
      if (e.target.closest && e.target.closest('[data-wr-link]')) {
        e.stopPropagation();
      }
    };

    ['pointerdown', 'mousedown', 'touchstart'].forEach((type) => {
      this.addEventListener(type, stop, { capture: true, passive: true });
    });
  }

  /* Heel de kaart is klikbaar, niet alleen de pil. De pil blijft de echte
     link (toetsenbord, screenreader, tikzone); een klik elders op het beeld
     van de kaart wordt daar naartoe doorgestuurd.

     Een sleepbeweging eindigt ook in een klik. Daarom onthouden we waar de
     vinger of muis neerkwam en sturen we alleen door als de verplaatsing
     kleiner is dan een paar pixels. Slepen blijft dus slepen.

     Klikken op de pil zelf of op een andere link/knop laten we met rust.
     Cmd-, ctrl- of shift-klik opent de link in een nieuw tabblad. */
  kaartKlik() {
    if (this._kaartKlikBewaakt) return;
    this._kaartKlikBewaakt = true;

    let start = null;
    const DREMPEL = 8;

    this.addEventListener('pointerdown', (e) => {
      start = { x: e.clientX, y: e.clientY };
    }, { capture: true, passive: true });

    this.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      const doel = e.target;
      if (!doel.closest || doel.closest('a, button')) return;
      if (!doel.closest('.wr-kaart__media')) return;

      const link = doel.closest('.wr-kaart')?.querySelector('a[data-wr-link]');
      if (!link) return;

      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > DREMPEL) return;

      /* Rechtstreeks navigeren in plaats van een nagebootste klik op de link
         te sturen: een klik die binnen een andere klik wordt verstuurd, voert
         de browser niet uit. */
      e.preventDefault();
      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        window.open(link.href, '_blank', 'noopener');
      } else {
        window.location.assign(link.href);
      }
    });
  }

  /* Het slider-script kloont kaarten voor de loop en zet er aria-hidden op.
     Onze kaarten zijn links, en die blijven focusbaar: met het toetsenbord tab
     je dan door verborgen dubbele links. aria-hidden op een element met
     focusbare inhoud is bovendien in strijd met de ARIA-spec.

     Oplossing: tabindex -1 op alles wat focus kan krijgen binnen een kloon.
     Muisgebruikers kunnen elke zichtbare kaart nog aanklikken, toetsenbord en
     screenreaders lopen alleen langs de originelen.

     Een MutationObserver, want het script maakt de klonen ook opnieuw aan bij
     elke resize, buiten onze init om. */
  bewaakKlonen() {
    const patch = () => {
      this.querySelectorAll('[data-radial-slider-clone]').forEach((kloon) => {
        kloon.querySelectorAll('a, button, [tabindex]').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
      });
    };

    patch();

    const lijst = this.querySelector('[data-radial-slider-list]');
    if (!lijst || this.kloonObserver) return;

    this.kloonObserver = new MutationObserver(patch);
    this.kloonObserver.observe(lijst, { childList: true });
  }

  /* Laadt een script eenmalig. Meerdere secties op dezelfde pagina delen dus
     dezelfde GSAP-instantie in plaats van hem opnieuw op te halen. */
  laadScript(src, globalNaam) {
    if (!src) return Promise.reject(new Error('script-URL ontbreekt'));
    if (globalNaam && window[globalNaam]) return Promise.resolve();

    WereldRadial.laadCache = WereldRadial.laadCache || new Map();
    if (WereldRadial.laadCache.has(src)) return WereldRadial.laadCache.get(src);

    const belofte = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.async = false; // volgorde bewaren
      el.onload = () => resolve();
      el.onerror = () => reject(new Error(`kon ${src} niet laden`));
      document.head.appendChild(el);
    });

    WereldRadial.laadCache.set(src, belofte);
    return belofte;
  }

  /* Het Osmo-script hangt zijn Draggable en proxy-element aan de container.
     Bij unload moeten die weg, anders blijven ze in het geheugen hangen. */
  opruimen() {
    clearTimeout(this._sleepTimer);

    if (this._radialSliderDraggable) {
      /* Zie sleepUitOpTouch: het kan één instantie of een array zijn, en
         .kill() op een array is undefined. */
      const bron = this._radialSliderDraggable;
      (Array.isArray(bron) ? bron : [bron]).forEach((i) => i?.kill?.());
      this._radialSliderDraggable = null;
    }
    if (this._radialSliderProxy && window.gsap) {
      window.gsap.killTweensOf(this._radialSliderProxy);
    }
    if (this._radialSliderProxyEl) {
      this._radialSliderProxyEl.remove();
      this._radialSliderProxyEl = null;
    }
    this.querySelectorAll('[data-radial-slider-clone]').forEach((el) => el.remove());
  }
}

if (!customElements.get('wereld-radial')) {
  customElements.define('wereld-radial', WereldRadial);
}

/* --- Theme editor ------------------------------------------------------- */

document.addEventListener('shopify:section:load', (event) => {
  const el = event.target.querySelector('wereld-radial');
  if (!el) return;
  // connectedCallback loopt al bij het opnieuw invoegen; dit dekt het geval
  // waarin het element bleef bestaan maar de inhoud veranderde
  if (el.geinitialiseerd && typeof window.initRadialCardsSlider === 'function') {
    el.opruimen();
    window.initRadialCardsSlider();
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelector('wereld-radial')?.opruimen();
});

/* Blokken toevoegen of verplaatsen hertekent de sectie niet altijd volledig */
document.addEventListener('shopify:block:select', (event) => {
  const el = event.target.closest('wereld-radial');
  if (el && typeof window.initRadialCardsSlider === 'function') {
    window.initRadialCardsSlider();
  }
});
